
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReviewForm from './ReviewForm';

interface Review {
  id: string;
  name: string;
  location: string;
  rating: number;
  review_text: string;
  service: string;
  created_at: string;
}

export default function ReviewSlider() {
  const { t } = useTranslation();
  const [currentReview, setCurrentReview] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fallback reviews for when no database reviews are available
  const fallbackReviews = [
    {
      id: 'fallback-1',
      name: 'Sarah Johnson',
      location: 'Buckhead, Atlanta',
      rating: 5,
      review_text: t('review1Text'),
      service: t('review1Service'),
      created_at: '2024-01-01'
    },
    {
      id: 'fallback-2',
      name: 'Michael Rodriguez',
      location: 'Midtown, Atlanta',
      rating: 5,
      review_text: t('review2Text'),
      service: t('review2Service'),
      created_at: '2024-01-02'
    },
    {
      id: 'fallback-3',
      name: 'Jennifer Chen',
      location: 'Decatur, Atlanta',
      rating: 5,
      review_text: t('review3Text'),
      service: t('review3Service'),
      created_at: '2024-01-03'
    },
    {
      id: 'fallback-4',
      name: 'David Thompson',
      location: 'Sandy Springs, Atlanta',
      rating: 5,
      review_text: t('review4Text'),
      service: t('review4Service'),
      created_at: '2024-01-04'
    },
    {
      id: 'fallback-5',
      name: 'Lisa Martinez',
      location: 'Marietta, Atlanta',
      rating: 5,
      review_text: t('review5Text'),
      service: t('review5Service'),
      created_at: '2024-01-05'
    },
    {
      id: 'fallback-6',
      name: 'Robert Kim',
      location: 'Alpharetta, Atlanta',
      rating: 5,
      review_text: t('review6Text'),
      service: t('review6Service'),
      created_at: '2024-01-06'
    }
  ];

  useEffect(() => {
    fetchApprovedReviews();
  }, []);

  const fetchApprovedReviews = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-approved-reviews`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.reviews.length > 0) {
          setReviews(result.reviews);
        } else {
          // Use fallback reviews if no approved reviews found
          setReviews(fallbackReviews);
        }
      } else {
        // Use fallback reviews if API call fails
        setReviews(fallbackReviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Use fallback reviews if there's an error
      setReviews(fallbackReviews);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (reviews.length > 0) {
      const timer = setInterval(() => {
        setCurrentReview((prev) => (prev + 1) % reviews.length);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [reviews.length]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`ri-star-${index < rating ? 'fill' : 'line'} text-yellow-400`}
      />
    ));
  };

  const getCustomerImage = (name: string, index: number) => {
    const imageQueries = [
      'Professional headshot of satisfied female customer, middle-aged woman smiling, friendly expression, home owner portrait, natural lighting, professional photo',
      'Professional headshot of satisfied male customer, middle-aged man smiling, business professional portrait, confident expression, natural lighting',
      'Professional headshot of satisfied Asian female customer, young professional woman smiling, friendly portrait, modern style, natural lighting',
      'Professional headshot of satisfied male customer, older gentleman smiling, distinguished appearance, business portrait, natural lighting',
      'Professional headshot of satisfied Hispanic female customer, mature woman smiling, warm expression, professional portrait, natural lighting',
      'Professional headshot of satisfied Asian male customer, middle-aged professional man smiling, confident portrait, business style, natural lighting'
    ];
    
    const queryIndex = index % imageQueries.length;
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28imageQueries%5BqueryIndex%5D%29%7D&width=80&height=80&seq=review${index + 1}&orientation=squarish`;
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
            {t('reviewsTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            {t('reviewsSubtitle')}
          </p>
          <ReviewForm />
        </div>

        {reviews.length > 0 && (
          <>
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="relative h-80 lg:h-64">
                  {reviews.map((review, index) => (
                    <div
                      key={review.id}
                      className={`absolute inset-0 transition-all duration-700 transform ${
                        index === currentReview
                          ? 'opacity-100 translate-x-0'
                          : index < currentReview
                          ? 'opacity-0 -translate-x-full'
                          : 'opacity-0 translate-x-full'
                      }`}
                    >
                      <div className="p-8 lg:p-12 h-full flex flex-col justify-center">
                        <div className="flex items-center mb-6">
                          <img
                            src={getCustomerImage(review.name, index)}
                            alt={review.name}
                            className="w-16 h-16 rounded-full object-cover object-top mr-4"
                          />
                          <div>
                            <h3 className="text-xl font-semibold text-gray-800">
                              {review.name}
                            </h3>
                            <p className="text-gray-600">{review.location}</p>
                            <div className="flex items-center mt-1">
                              {renderStars(review.rating)}
                            </div>
                          </div>
                        </div>
                        
                        <blockquote className="text-lg text-gray-700 mb-4 italic leading-relaxed">
                          "{review.review_text}"
                        </blockquote>
                        
                        <div className="text-sm text-blue-600 font-medium">
                          {review.service}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center mt-8 space-x-2">
                {reviews.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentReview(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentReview ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={() => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <button
                onClick={() => setCurrentReview((prev) => (prev + 1) % reviews.length)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xl text-gray-600"></i>
              </button>
            </div>

            {/* Review Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">{t('reviewsCompleted')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">4.9</div>
                <div className="text-gray-600">{t('reviewsRating')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">98%</div>
                <div className="text-gray-600">{t('reviewsSatisfaction')}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
